import { Box, Button, Flex, HStack, Icon, Input, Switch, Text, VStack } from "@chakra-ui/react";
import { HiOutlineMinus, HiOutlinePlus, HiOutlineTrash, HiOutlineChevronDown } from "react-icons/hi2";

const MessageEditor = ({ message, onChange, onRemove, index, collapsed, onToggleCollapsed }) => {
    const update = (patch) => onChange({ ...message, ...patch });
    const updateOutput = (patch) => update({ output: { ...message.output, ...patch } });

    const handleInputNullToggle = (checked) => {
        update({ input: checked ? null : "" });
    };

    const handleExactResponseNullToggle = (checked) => {
        updateOutput({ exactResponse: checked ? null : "" });
    };

    const addPossibleResponse = () => {
        updateOutput({ possibleResponses: [...message.output.possibleResponses, ""] });
    };

    const updatePossibleResponse = (i, value) => {
        const updated = [...message.output.possibleResponses];
        updated[i] = value;
        updateOutput({ possibleResponses: updated });
    };

    const removePossibleResponse = (i) => {
        updateOutput({ possibleResponses: message.output.possibleResponses.filter((_, idx) => idx !== i) });
    };

    const inputIsNull = message.input === null;
    const exactIsNull = message.output.exactResponse === null;

    return (
        <Box w="full" bg="bg.dark" borderRadius={10} p={4} borderWidth="1px" borderColor="border.primary">
            <Flex justifyContent="space-between" alignItems="center" mb={collapsed ? 0 : 3}>
                <Flex alignItems="center" gap={2} cursor="pointer" onClick={onToggleCollapsed} flex={1} userSelect="none" minW={0}>
                    <Icon fontSize={14} color="text.grey" style={{ transform: collapsed ? "rotate(-90deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }}>
                        <HiOutlineChevronDown />
                    </Icon>
                    <Text color="text.grey" fontSize={13} fontWeight="semibold" noOfLines={1}>
                        Message #{index + 1}{collapsed && message.input !== null && message.input ? ` — ${message.input}` : collapsed && message.input === null ? " — null" : ""}
                    </Text>
                </Flex>
                <Icon fontSize={18} color="red.400" cursor="pointer" onClick={onRemove} flexShrink={0} ml={2}>
                    <HiOutlineTrash />
                </Icon>
            </Flex>

            {!collapsed && <VStack gap={3} alignItems="flex-start" w="full">
                {/* Input */}
                <Box w="full">
                    <HStack mb={1} justifyContent="space-between">
                        <Text color="white" fontSize={13}>Input</Text>
                        <HStack gap={2}>
                            <Text color="text.grey" fontSize={12}>null</Text>
                            <Switch.Root checked={inputIsNull} onCheckedChange={(e) => handleInputNullToggle(e.checked)} size="sm">
                                <Switch.HiddenInput />
                                <Switch.Control bg={inputIsNull ? "purple.500" : "bg.secondary"}>
                                    <Switch.Thumb />
                                </Switch.Control>
                            </Switch.Root>
                        </HStack>
                    </HStack>
                    {!inputIsNull && (
                        <Input
                            variant="primary"
                            size="sm"
                            placeholder="Enter user input"
                            value={message.input ?? ""}
                            onChange={(e) => update({ input: e.target.value })}
                        />
                    )}
                    {inputIsNull && (
                        <Box px={3} py={2} bg="bg.secondary" borderRadius={8} borderWidth="1px" borderColor="border.primary">
                            <Text color="text.grey" fontSize={12} fontStyle="italic">null (first message)</Text>
                        </Box>
                    )}
                </Box>

                {/* exactResponse */}
                <Box w="full">
                    <HStack mb={1} justifyContent="space-between">
                        <Text color="white" fontSize={13}>Exact Response</Text>
                        <HStack gap={2}>
                            <Text color="text.grey" fontSize={12}>null</Text>
                            <Switch.Root checked={exactIsNull} onCheckedChange={(e) => handleExactResponseNullToggle(e.checked)} size="sm">
                                <Switch.HiddenInput />
                                <Switch.Control bg={exactIsNull ? "purple.500" : "bg.secondary"}>
                                    <Switch.Thumb />
                                </Switch.Control>
                            </Switch.Root>
                        </HStack>
                    </HStack>
                    {!exactIsNull && (
                        <Input
                            variant="primary"
                            size="sm"
                            placeholder="Expected exact response"
                            value={message.output.exactResponse ?? ""}
                            onChange={(e) => updateOutput({ exactResponse: e.target.value })}
                        />
                    )}
                    {exactIsNull && (
                        <Box px={3} py={2} bg="bg.secondary" borderRadius={8} borderWidth="1px" borderColor="border.primary">
                            <Text color="text.grey" fontSize={12} fontStyle="italic">null (any response accepted)</Text>
                        </Box>
                    )}
                </Box>

                {/* possibleResponses */}
                <Box w="full">
                    <HStack mb={2} justifyContent="space-between">
                        <Text color="white" fontSize={13}>Possible Responses</Text>
                        <Button size="xs" variant="outlined" onClick={addPossibleResponse} px={2} py={1} h="auto">
                            <Icon fontSize={14}><HiOutlinePlus /></Icon>
                            <Text fontSize={12}>Add</Text>
                        </Button>
                    </HStack>
                    <VStack gap={2} alignItems="flex-start" w="full">
                        {message.output.possibleResponses.map((resp, i) => (
                            <HStack key={i} w="full" gap={2}>
                                <Input
                                    variant="primary"
                                    size="sm"
                                    flex={1}
                                    placeholder={`Possible response ${i + 1}`}
                                    value={resp}
                                    onChange={(e) => updatePossibleResponse(i, e.target.value)}
                                />
                                <Icon fontSize={16} color="red.400" cursor="pointer" onClick={() => removePossibleResponse(i)}>
                                    <HiOutlineMinus />
                                </Icon>
                            </HStack>
                        ))}
                        {message.output.possibleResponses.length === 0 && (
                            <Text color="text.grey" fontSize={12} fontStyle="italic">No possible responses defined</Text>
                        )}
                    </VStack>
                </Box>

                {/* anyOfPhrases */}
                <Box w="full">
                    <Text color="white" fontSize={13} mb={1}>Any of Phrases</Text>
                    <Input
                        variant="primary"
                        size="sm"
                        placeholder="np. poniedziałek, koszt 50zł, Prześlij dokument (oddzielone przecinkami)"
                        value={message.output.anyOfPhrases ?? ""}
                        onChange={(e) => updateOutput({ anyOfPhrases: e.target.value || null })}
                        opacity={!exactIsNull || message.output.possibleResponses.length > 0 ? 0.4 : 1}
                        isDisabled={!exactIsNull || message.output.possibleResponses.length > 0}
                    />
                    {(!exactIsNull || message.output.possibleResponses.length > 0) && (
                        <Text color="text.grey" fontSize={11} mt={1}>Niedostępne gdy ustawiono Exact Response lub Possible Responses</Text>
                    )}
                </Box>

                {/* isPositive */}
                <HStack gap={3}>
                    <Text color="white" fontSize={13}>Is Positive</Text>
                    <Switch.Root
                        checked={message.output.isPositive}
                        onCheckedChange={(e) => updateOutput({ isPositive: e.checked })}
                        size="sm"
                    >
                        <Switch.HiddenInput />
                        <Switch.Control bg={message.output.isPositive ? "green.500" : "bg.secondary"}>
                            <Switch.Thumb />
                        </Switch.Control>
                    </Switch.Root>
                    <Text color="text.grey" fontSize={12}>{message.output.isPositive ? "Yes" : "No"}</Text>
                </HStack>

                {/* expectedEntityId */}
                <Box w="full">
                    <Text color="white" fontSize={13} mb={1}>Expected Entity ID</Text>
                    <Input
                        variant="primary"
                        size="sm"
                        placeholder="UUID of expected entity"
                        value={message.output.expectedEntityId}
                        onChange={(e) => updateOutput({ expectedEntityId: e.target.value })}
                    />
                </Box>
            </VStack>}
        </Box>
    );
};

export default MessageEditor;
